import styled from '@emotion/styled-base';
import * as Sentry from '@sentry/browser';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { navigationService, userService } from 'src/config/rootService';
import computerDeskIllustrationUrl from 'src/public/ds/illustrations/marginalia-girl-computer.svg';
import { Button } from 'src/shared/components/Button';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { color, font } from 'src/shared/styles/variables';
import { useGetCompanyInsuranceCarrierLazyQuery } from '../documents/GetCompanyInsuranceCarrier.generated';

interface IllustrationProps {
    illustrationUrl: string;
}

export enum CheckForInsurancePageTestID {
    Wrapper = 'CheckForInsurancePageTestID.Wrapper',
    NoButton = 'CheckForInsurancePageTestID.NoButton',
}

const PageContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 524,
    marginTop: '3rem',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingBottom: '3rem',
});

const ButtonContainer = styled('div')({
    display: 'flex',
    width: '100%',
    gap: 18,
});

const ComputerDeskIllustration = styled('div')<IllustrationProps>(({ illustrationUrl }) => ({
    display: 'inline-block',
    width: 300,
    height: 300,
    backgroundImage: `url('${illustrationUrl}')`,
    backgroundSize: 'cover',
    backgroundPositionX: 'center',
}));

const StyledTitle = styled('h1')(
    {
        color: color.ebonyClay,
        marginBottom: 28,
    },
    font.heavy(font.size.extraLarge),
);

const StyledPara = styled('p')(
    {
        marginBottom: 28,
        color: color.blueBayoux,
    },
    font.book(font.size.medium),
);

export const CheckForInsurancePage = () => {
    const [insuranceName, setInsuranceName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [getCarrierName, { data }] = useGetCompanyInsuranceCarrierLazyQuery();
    const { t } = useTranslation();

    const asyncGetCompanyAndCarrier = async () => {
        try {
            const {
                userDetails: { company, companyUuid },
            } = await userService.fetchUserInfo();

            await getCarrierName({ variables: { employerUuid: companyUuid } });

            setCompanyName(company);
        } catch (err) {
            err.name = 'Error retrieving company name and/or company insurance carrier(s)';
            Sentry.captureException(err);
            navigationService.replace('/dashboard');
        }
    };

    useEffect(() => {
        if (data && data.getEmployerInsurance && !data.getEmployerInsurance.name) {
            navigationService.replace('/dashboard');
        }

        const companyCarrierName = data?.getEmployerInsurance?.name;

        setInsuranceName(companyCarrierName || '');
    }, [data]);

    useEffect(() => {
        asyncGetCompanyAndCarrier();
    }, []);

    if (!(companyName && insuranceName)) {
        return (
            <PageContainer>
                <CenteredSpinner />
            </PageContainer>
        );
    }

    return (
        <PageContainer data-testid={CheckForInsurancePageTestID.Wrapper}>
            <ComputerDeskIllustration illustrationUrl={computerDeskIllustrationUrl} />
            <StyledTitle>
                {t('insuranceForm:insurance_inquiry.do_you_have_insurance_q', {
                    companyName,
                    insuranceName,
                })}
            </StyledTitle>
            <StyledPara>{t('insuranceForm:insurance_inquiry.check_plan_coverage_text')}</StyledPara>
            <StyledPara>
                {t('insuranceForm:insurance_inquiry.diagnosis_and_confidentiality_text')}
            </StyledPara>
            <ButtonContainer>
                <Button
                    fullWidth
                    type="white"
                    testID={CheckForInsurancePageTestID.NoButton}
                    to={{
                        pathname: '/youre-on-your-way',
                        state: {
                            title: t('insuranceForm:insurance_optout.page_title'),
                            description: t('insuranceForm:insurance_optout.page_description'),
                            componentKey: 'contact_us',
                        },
                    }}
                    replace={true}
                >
                    {t('providers:demographic_feedback_modal.answer.no')}
                </Button>
                <Button fullWidth to="/enter-insurance-plan-information" type="accept">
                    {t('providers:demographic_feedback_modal.answer.yes')}
                </Button>
            </ButtonContainer>
        </PageContainer>
    );
};
